import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { ClockFading } from "lucide-react";
import OpenSign from "./openSign";
import { openingHoursSelector } from "../modules/store/selectors";
import {
  OpeningHoursContent,
  OpeningHoursEmpty,
  StoreStatusButton,
} from "../styles";
import { formatTime } from "../utils/format";

function OpeningHoursPopover() {
  const [open, setOpen] = useState(false);
  const hours = useSelector(openingHoursSelector);
  const anchor = useRef(null);
  const trigger = useRef(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (!anchor.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      className="opening-hours-anchor"
      ref={anchor}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <OpenSign>
        {({ text, isOpen }) => (
          <StoreStatusButton
            ref={trigger}
            type="button"
            $open={isOpen}
            onClick={() => setOpen((value) => !value)}
            aria-label={`Godziny otwarcia. ${text}`}
            aria-expanded={open}
            aria-controls="opening-hours-popover"
          >
            <span>Godziny otwarcia</span>
            <small>{isOpen ? "Otwarte" : "Zamknięte"}</small>
          </StoreStatusButton>
        )}
      </OpenSign>
      {open && (
        <section
          id="opening-hours-popover"
          className="opening-hours-popover"
          aria-label="Godziny otwarcia"
        >
          <h2>Godziny otwarcia</h2>
          <OpeningHoursContent>
            {hours.length === 0 && (
              <OpeningHoursEmpty>
                <ClockFading size={28} strokeWidth={1.75} aria-hidden="true" />
                <p>Cierpliwości, niedługo coś się tu pojawi.</p>
              </OpeningHoursEmpty>
            )}
            {hours.map((hour, index) => (
              <div
                key={`${hour.weekday}-${index}`}
                className="d-flex justify-content-between"
              >
                <div>
                  {
                    [
                      "Poniedziałek",
                      "Wtorek",
                      "Środa",
                      "Czwartek",
                      "Piątek",
                      "Sobota",
                      "Niedziela",
                    ][hour.weekday]
                  }
                </div>
                <div>
                  {formatTime(hour.start)} &mdash; {formatTime(hour.end)}
                </div>
              </div>
            ))}
          </OpeningHoursContent>
        </section>
      )}
    </div>
  );
}

export default OpeningHoursPopover;
